import { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGraphStats } from '../../../api/graph';

interface MiniNode {
  id: string;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
}

interface MiniEdge {
  source: number;
  target: number;
}

const TYPE_COLORS: Record<string, string> = {
  person: '#3b82f6',
  organization: '#a855f7',
  location: '#22c55e',
  ip_address: '#ef4444',
  domain: '#f97316',
  email: '#14b8a6',
  other: '#94a3b8',
};

export default function MiniGraphWidget() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { data: graphStats } = useGraphStats();
  const animRef = useRef<number>(0);

  // Generate mock nodes from stats
  const { nodes, edges } = useMemo(() => {
    const counts = graphStats?.entity_type_counts ?? {};
    const nodeList: MiniNode[] = [];
    const edgeList: MiniEdge[] = [];

    let idx = 0;
    for (const [type, count] of Object.entries(counts)) {
      const numNodes = Math.min(count as number, 5);
      for (let i = 0; i < numNodes; i++) {
        nodeList.push({
          id: `${type}-${i}`,
          label: type,
          x: Math.random() * 280 + 10,
          y: Math.random() * 160 + 10,
          vx: 0,
          vy: 0,
          color: TYPE_COLORS[type] ?? TYPE_COLORS.other,
        });
        // connect to random previous node
        if (idx > 0 && Math.random() > 0.3) {
          edgeList.push({ source: idx, target: Math.floor(Math.random() * idx) });
        }
        idx++;
      }
    }

    // If no data, create placeholder nodes
    if (nodeList.length === 0) {
      for (let i = 0; i < 8; i++) {
        nodeList.push({
          id: `placeholder-${i}`,
          label: 'entity',
          x: Math.random() * 280 + 10,
          y: Math.random() * 160 + 10,
          vx: 0,
          vy: 0,
          color: '#64748b',
        });
        if (i > 0) edgeList.push({ source: i, target: Math.floor(Math.random() * i) });
      }
    }

    return { nodes: nodeList, edges: edgeList };
  }, [graphStats]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    const tick = () => {
      // Simple force simulation
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        // Center gravity
        n.vx += (W / 2 - n.x) * 0.001;
        n.vy += (H / 2 - n.y) * 0.001;

        // Repulsion from other nodes
        for (let j = i + 1; j < nodes.length; j++) {
          const m = nodes[j];
          const dx = n.x - m.x;
          const dy = n.y - m.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          if (dist < 60) {
            const f = (60 - dist) * 0.02;
            const fx = (dx / dist) * f;
            const fy = (dy / dist) * f;
            n.vx += fx;
            n.vy += fy;
            m.vx -= fx;
            m.vy -= fy;
          }
        }
      }

      // Edge attraction
      for (const e of edges) {
        const s = nodes[e.source];
        const t = nodes[e.target];
        if (!s || !t) continue;
        const dx = t.x - s.x;
        const dy = t.y - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const f = (dist - 50) * 0.003;
        s.vx += (dx / dist) * f;
        s.vy += (dy / dist) * f;
        t.vx -= (dx / dist) * f;
        t.vy -= (dy / dist) * f;
      }

      // Apply velocity
      for (const n of nodes) {
        n.vx *= 0.9;
        n.vy *= 0.9;
        n.x = Math.max(10, Math.min(W - 10, n.x + n.vx));
        n.y = Math.max(10, Math.min(H - 10, n.y + n.vy));
      }

      // Draw
      ctx.clearRect(0, 0, W, H);

      // Edges
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.3)';
      ctx.lineWidth = 1;
      for (const e of edges) {
        const s = nodes[e.source];
        const t = nodes[e.target];
        if (!s || !t) continue;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
        ctx.stroke();
      }

      // Nodes
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      animRef.current = requestAnimationFrame(tick);
    };

    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [nodes, edges]);

  return (
    <div className="glass-panel p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-300">Apercu du graphe</h3>
        <button
          onClick={() => navigate('/graph')}
          className="text-xs text-cyber-cyan hover:underline"
        >
          Explorer
        </button>
      </div>
      <div
        className="flex-1 min-h-0 cursor-pointer rounded-lg overflow-hidden"
        onClick={() => navigate('/graph')}
      >
        <canvas
          ref={canvasRef}
          width={300}
          height={180}
          className="w-full h-full"
          style={{ imageRendering: 'auto' }}
        />
      </div>
    </div>
  );
}
